import { EditableTitle } from "@/components/shared/EditableTitle";
import { useElementWidth } from "@/hooks/useElementWidth";
import { cn } from "@paribus/shared-ui";
import { ChevronRight } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

const COMPACT_MAX_WIDTH = 860;

export interface PageHeaderProps {
  breadcrumb?: { label: string; to?: string }[];
  title: string;
  onTitleChange?: (next: string) => void;
  statusBadge?: React.ReactNode;
  meta?: React.ReactNode[];
  /** `inline`: meta sits beside the title on the same row. `below` (default): its own row underneath. */
  metaPlacement?: "below" | "inline";
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  breadcrumb,
  title,
  onTitleChange,
  statusBadge,
  meta,
  metaPlacement = "below",
  actions,
  className,
}) => {
  const [rowRef, rowWidth] = useElementWidth<HTMLDivElement>();
  const isCompact = rowWidth < COMPACT_MAX_WIDTH;

  const metaRow = meta && meta.length > 0 && (
    <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      {meta.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span className="text-border-strong" aria-hidden>
              ·
            </span>
          )}
          <span className="tabular-nums">{item}</span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className={cn("mb-2", className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight size={12} className="text-border-strong" aria-hidden />}
              {crumb.to ? (
                <Link to={crumb.to} className="transition-colors hover:text-foreground">
                  {crumb.label}
                </Link>
              ) : (
                <span>{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div ref={rowRef} className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <EditableTitle
              value={title}
              onSave={onTitleChange}
              as="h1"
              sizeClassName={isCompact ? "text-xl" : "text-2xl"}
              truncate={isCompact}
              editAriaLabel="Edit name"
              editTooltip
              inputFullWidth
            />
            {metaPlacement === "inline" && metaRow}
            {statusBadge}
          </div>

          {metaPlacement === "below" && metaRow && <div className="mt-0.5">{metaRow}</div>}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
      </div>
    </div>
  );
};
